import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';

import { get } from '@lumapps/constants';
import type { RecursivePartial } from '@lumapps/utils/types/RecursivePartial';

import type { User } from '../types';

const { userLang } = get();

/*
 * Variable used for defining the languages which reverts user's first and last name
 */
const REVERTED_FULL_NAME_LANGS = ['hu', 'ja'];

/**
 * Very partial user definition used to generate the full name.
 */
type PartialUser = RecursivePartial<Pick<User, 'fullName' | 'firstName' | 'lastName' | 'apiProfile'>>;

/**
 * Get the user full name adapted to the current language.
 *
 * @deprecated You should not this function directly, use the components `UserAvatar`, `UserBlock` or `UserLink` instead. If needed the `useUser` hook does the same.
 * @param  [user] The user or author to return full name from.
 * @param  [language] The current language to know whether we should reverse first and last names (defaults to the current user lang)
 * @return The formatted user full name.
 */
export function getUserFullName(
    user: PartialUser | undefined | null,
    language: string = userLang,
    fallback: string = '',
): string {
    if (!user || isEmpty(user)) {
        return fallback;
    }

    const hasReversedFullName = includes(REVERTED_FULL_NAME_LANGS, language);
    const canComputeName = Boolean(
        user.firstName || user.apiProfile?.name?.givenName || user.lastName || user.apiProfile?.name?.familyName,
    );
    /**
     * If the user fullName exists, it means that the provider returns the right information, simply return it.
     */
    // eslint-disable-next-line deprecation/deprecation
    if (user.fullName && (!hasReversedFullName || !canComputeName)) {
        // eslint-disable-next-line deprecation/deprecation
        return user.fullName;
    }

    /**
     * If the user fullName doesn't exists, there's no way to get the current lang
     * in the backend and know the reversed names.
     * We use user's firstName and lastName to display the right user fullName.
     */
    const fullNameArray = [];
    fullNameArray.push(user.firstName || user.apiProfile?.name?.givenName);
    fullNameArray.push(user.lastName || user.apiProfile?.name?.familyName);

    return hasReversedFullName ? fullNameArray.reverse().join(' ').trim() : fullNameArray.join(' ').trim();
}
